import { useEffect, useMemo, useState } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { format } from 'date-fns';
import { StatTile } from '../../components/StatTile';
import DataTable from '../../components/DataTable';
import { getClinicalSettings, saveClinicalSettings, assessmentStatus } from '../../lib/clinicalSettings';
import { CLOSED_STATUS_FILTER } from '../../lib/serviceModel';

const ALLOWED_ROLES = ['coordinator', 'manager', 'admin'];

export default function Assessments() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const canSendLinks = ALLOWED_ROLES.includes(profile?.role);

  const [clients, setClients]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [sortKey, setSortKey]   = useState('last_name');
  const [sortAsc, setSortAsc]   = useState(true);
  const [filter, setFilter]     = useState('All');
  const [settings, setSettings] = useState(null);
  useEffect(() => { getClinicalSettings().then(setSettings); }, []);
  const statusOf = (c) => settings ? assessmentStatus(c.nurse_assessment_date || c.assessment_date, settings) : null;

  // ── Send Link modal state ─────────────────────────────────────
  const [linkModal, setLinkModal] = useState(null); // null | { client }
  const [nurseEmail, setNurseEmail] = useState('');
  const [nurseName, setNurseName]   = useState('');
  const [linkSending, setLinkSending] = useState(false);
  const [linkResult, setLinkResult]   = useState(null); // null | 'sent' | 'error'
  const [linkError, setLinkError]     = useState('');

  // ── Active links (pending, not revoked, not expired) ─────────
  const [activeLinks, setActiveLinks] = useState({}); // { client_db_id: true }

  // ── Assessment documents (Google Drive links) ─────────────────
  const [docLinks, setDocLinks] = useState({}); // { client_db_id: { url, name, date } }

  // ── Revoke modal ───────────────────────────────────────────────
  const [revokeModal, setRevokeModal]   = useState(null); // null | { client }
  const [revoking, setRevoking]         = useState(false);
  const { toast, showToast, hideToast } = useToast(4000);

  const openLinkModal = (e, client) => {
    e.stopPropagation();
    setLinkModal(client);
    setNurseEmail('');
    setNurseName('');
    setLinkResult(null);
    setLinkError('');
  };
  const closeLinkModal = () => {
    setLinkModal(null);
    setLinkResult(null);
  };

  const handleSendLink = async () => {
    if (!nurseEmail.trim()) return;
    setLinkSending(true);
    setLinkResult(null);
    setLinkError('');
    try {
      // 1. Create the assessment link record
      const { data: link, error: linkErr } = await supabase
        .from('assessment_links')
        .insert([{
          client_id:       linkModal.client_id,
          client_db_id:    linkModal.id,
          nurse_email:     nurseEmail.trim().toLowerCase(),
          nurse_name:      nurseName.trim() || null,
          created_by_id:   profile.id,
          created_by_name: profile.full_name || null,
        }])
        .select('token, expires_at')
        .single();

      if (linkErr || !link) throw new Error(linkErr?.message || 'Failed to create link');

      const formUrl = `${window.location.origin}/nurse-form/${link.token}`;
      const expiry  = format(new Date(link.expires_at), 'MMMM d, yyyy');
      const patient = `${linkModal.first_name} ${linkModal.last_name}`;

      // 2. Send email via existing send-email edge function
      const { data: { session } } = await supabase.auth.getSession();
      const emailRes = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            to: nurseEmail.trim().toLowerCase(),
            // Send only to the nurse — no team CC/BCC so the recipient is unambiguous.
            bypass_central: true,
            subject: `Nurse Assessment Form — ${patient}`,
            html: `
              <div style="font-family:'Trebuchet MS',sans-serif;max-width:600px;margin:0 auto;background:#EAF0F8;padding:24px;">
                <div style="background:#1A3A6B;border-radius:10px 10px 0 0;padding:20px 28px;">
                  <div style="color:#fff;font-family:Georgia,serif;font-size:18px;font-weight:700;">A Heart For Care</div>
                  <div style="color:#B9CBE0;font-size:12px;margin-top:4px;">GAPP Unified Nurse Assessment</div>
                </div>
                <div style="background:#fff;border:1px solid #B9CBE0;border-top:none;border-radius:0 0 10px 10px;padding:28px;">
                  <p style="color:#1A3A6B;font-size:15px;font-weight:700;margin:0 0 12px;">
                    Hello${nurseName.trim() ? ` ${nurseName.trim()}` : ''},
                  </p>
                  <p style="color:#333;font-size:13px;line-height:1.7;margin:0 0 16px;">
                    You have been requested to complete a nurse assessment for patient <strong>${patient}</strong>.
                    Please click the button below to access the secure assessment form.
                  </p>
                  <div style="text-align:center;margin:28px 0;">
                    <a href="${formUrl}" style="background:#0DA06A;color:#fff;text-decoration:none;padding:14px 32px;border-radius:8px;font-weight:700;font-size:14px;display:inline-block;">
                      Open Assessment Form
                    </a>
                  </div>
                  <p style="color:#666;font-size:12px;line-height:1.6;margin:0 0 12px;">
                    <strong>Important:</strong><br/>
                    • This link is personal to you — please do not share it<br/>
                    • It expires on <strong>${expiry}</strong><br/>
                    • You can save your progress and return using the same link<br/>
                    • Once you submit, the link cannot be reused
                  </p>
                  <p style="color:#aaa;font-size:11px;margin:0;">
                    If you cannot click the button, copy and paste this URL into your browser:<br/>
                    <span style="color:#1A3A6B;word-break:break-all;">${formUrl}</span>
                  </p>
                </div>
                <p style="text-align:center;color:#aaa;font-size:10px;margin-top:14px;">
                  A Heart For Care · AHFC CRM · This is a secure, automated message
                </p>
              </div>
            `,
          }),
        }
      );

      const emailResult = await emailRes.json();
      if (!emailRes.ok || !emailResult.success) throw new Error(emailResult.error || 'Email failed to send');

      setLinkResult('sent');
      await loadData();
    } catch (err) {
      console.error('Send link error:', err);
      setLinkResult('error');
      setLinkError(err.message || 'An error occurred');
    } finally {
      setLinkSending(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeModal) return;
    setRevoking(true);
    try {
      const { error } = await supabase.from('assessment_links')
        .update({ revoked_at: new Date().toISOString(), revoked_by_name: profile?.full_name || 'Staff' })
        .eq('client_db_id', revokeModal.id)
        .is('used_at', null)
        .is('revoked_at', null);
      if (error) throw error;
      showToast(`Link revoked for ${revokeModal.first_name} ${revokeModal.last_name}`);
      setRevokeModal(null);
      await loadData();
    } catch (err) {
      console.error('Revoke error:', err);
      showToast('Failed to revoke link. Please try again.', 'error');
    } finally {
      setRevoking(false);
    }
  };

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  const loadData = async () => {
    const [clientsResult, linksResult, docsResult] = await Promise.all([
      supabase.from('clients')
        .select('id, client_id, first_name, last_name, current_status, nurse_assessment_complete, assessment_date, nurse_assessment_date, responsible_person_name, county, service_lines')
        .not('current_status', 'in', CLOSED_STATUS_FILTER)
        // A nurse assessment documents care in the home; supplies-only
        // clients have none to assess and would sit in "Pending" forever.
        .contains('service_lines', ['home_health'])
        .order('first_name'),
      supabase.from('assessment_links')
        .select('client_db_id')
        .is('used_at', null)
        .is('revoked_at', null)
        .gt('expires_at', new Date().toISOString()),
      supabase.from('nurse_assessments')
        .select('client_uuid, drive_document_url, updated_at')
        .not('drive_document_url', 'is', null),
    ]);
    if (clientsResult.error) console.error('Assessments query error:', clientsResult.error);
    setClients(clientsResult.data || []);
    const linkMap: Record<string, boolean> = {};
    (linksResult.data || []).forEach((l: any) => { linkMap[l.client_db_id] = true; });
    setActiveLinks(linkMap);
    // Map UUID (clients.id) → doc info directly via nurse_assessments.client_uuid
    const docMap: Record<string, any> = {};
    (docsResult.data || []).forEach((d: any) => {
      if (d.drive_document_url && d.client_uuid) {
        docMap[d.client_uuid] = { url: d.drive_document_url, date: d.updated_at };
      }
    });
    setDocLinks(docMap);
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sorted = [...clients].sort((a, b) => {
    let av = a[sortKey] ?? ''; let bv = b[sortKey] ?? '';
    if (typeof av === 'boolean') { av = av ? 1 : 0; bv = bv ? 1 : 0; }
    const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
    return sortAsc ? cmp : -cmp;
  });
  const filtered = filter === 'All' ? sorted
    : filter === 'Pending' ? sorted.filter(c => !c.nurse_assessment_complete)
    : filter === 'Review due' ? sorted.filter(c => c.nurse_assessment_complete && ['review_due', 'review_overdue', 'reassess_due', 'reassess_overdue'].includes(statusOf(c)?.key))
    : sorted.filter(c => c.nurse_assessment_complete);

  const stats = {
    total:    clients.length,
    complete: clients.filter(c => c.nurse_assessment_complete).length,
    pending:  clients.filter(c => !c.nurse_assessment_complete).length,
    due:      clients.filter(c => c.nurse_assessment_complete && ['review_due', 'review_overdue', 'reassess_due', 'reassess_overdue'].includes(statusOf(c)?.key)).length,
  };

  const columns = [
    {
      id: 'last_name', header: 'Client', size: 170,
      meta: { mobile: 'primary' },
      cell: info => { const c = info.row.original; return <span style={{ fontWeight: 500, color: 'var(--accent2)' }}>{c.last_name}, {c.first_name}</span>; },
    },
    {
      id: 'client_id', accessorKey: 'client_id', header: 'ID', size: 90,
      meta: { mono: true, mobile: 'secondary' },
      cell: info => info.getValue(),
    },
    {
      id: 'current_status', accessorKey: 'current_status', header: 'Status', size: 160,
      meta: { mobile: 'secondary' },
      cell: info => <span className="badge" style={{ fontSize: 11 }}>{info.getValue()}</span>,
    },
    {
      id: 'county', accessorKey: 'county', header: 'County', size: 110,
      meta: { mobile: 'secondary' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'responsible_person_name', accessorKey: 'responsible_person_name', header: 'Responsible', size: 140,
      meta: { mobile: 'hidden' },
      cell: info => <span style={{ color: 'var(--text2)' }}>{info.getValue() || '—'}</span>,
    },
    {
      id: 'nurse_assessment_complete', accessorKey: 'nurse_assessment_complete', header: 'Assessment', size: 130,
      meta: { mobile: 'primary' },
      cell: info => {
        const complete = info.getValue();
        return <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: complete ? 'var(--success-bg)' : 'var(--danger-bg)', color: complete ? 'var(--success)' : 'var(--danger)' }}>{complete ? '✓ Complete' : '⚠ Pending'}</span>;
      },
    },
    {
      id: 'assessment_date', accessorKey: 'assessment_date', header: 'Assessed · review', size: 200,
      meta: { mobile: 'secondary' },
      cell: info => {
        const c = info.row.original; const v = c.nurse_assessment_date || info.getValue(); const st = c.nurse_assessment_complete ? statusOf(c) : null;
        const tone = st ? ({ danger: 'var(--danger)', warn: 'var(--warn)', success: 'var(--success)' })[st.tone] : 'var(--text3)';
        return <div><span style={{ color: 'var(--text3)' }}>{v ? format(new Date(v), 'MMM d, yyyy') : '—'}</span>{st && <div style={{ fontSize: 11, color: tone, fontWeight: 600 }}>{st.label}{st.reviewDue ? ` · review ${format(st.reviewDue, 'MMM d')}` : ''}</div>}</div>;
      },
    },
    {
      id: 'document', header: 'Document', size: 120, enableSorting: false,
      meta: { mobile: 'hidden' },
      cell: info => {
        const doc = docLinks[info.row.original.id];
        return doc ? (
          <a
            href={doc.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
            title={`${doc.name || ''}${doc.date ? ' · ' + format(new Date(doc.date), 'MMM d, yyyy') : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, textDecoration: 'none', background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', color: 'var(--accent2)', whiteSpace: 'nowrap' }}
          >
            📄 View Doc
          </a>
        ) : <span style={{ color: 'var(--text3)', fontSize: 11 }}>—</span>;
      },
    },
    ...(canSendLinks ? [{
      id: 'assessment_link', header: 'Assessment Link', size: 200, enableSorting: false,
      meta: { mobile: 'secondary' },
      cell: info => {
        const c = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} onClick={e => e.stopPropagation()}>
            <button
              onClick={e => openLinkModal(e, c)}
              style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--accent-border)', background: 'var(--accent-bg)', color: 'var(--accent2)', whiteSpace: 'nowrap' }}
            >
              ✉ Send Link
            </button>
            {activeLinks[c.id] && (
              <button
                onClick={e => { e.stopPropagation(); setRevokeModal(c); }}
                style={{ padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', whiteSpace: 'nowrap' }}
              >
                🚫 Revoke
              </button>
            )}
          </div>
        );
      },
    }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        {[
          { label: 'Total Active', value: stats.total, tone: 'neutral' },
          { label: 'Assessment Complete', value: stats.complete, tone: 'neutral' },
          { label: 'Assessment Pending', value: stats.pending, tone: stats.pending > 0 ? 'warn' : 'neutral' },
          { label: `Review due (${settings?.assessment_review_days ?? 60}d / ${settings?.reassessment_days ?? 180}d)`, value: stats.due, tone: stats.due ? 'warn' : 'neutral' },
        ].map(s => <StatTile key={s.label} label={s.label} value={s.value} tone={s.tone} />)}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Nurse Assessments</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {['All', 'Pending', 'Review due', 'Complete'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#fff' : 'var(--text3)', borderColor: filter === f ? 'var(--accent)' : 'var(--border)' }}>{f}</button>
            ))}
            {settings && canSendLinks && (
              <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', fontSize: 11, color: 'var(--text3)', marginLeft: 8 }}>
                review every <input type="number" style={{ width: 52, fontSize: 11, padding: '2px 4px', borderRadius: 5, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} defaultValue={settings.assessment_review_days}
                  onBlur={e => { const v = Number(e.target.value); if (v > 0 && v !== settings.assessment_review_days) saveClinicalSettings({ assessment_review_days: v }).then(setSettings); }} />
                d · reassess every <input type="number" style={{ width: 52, fontSize: 11, padding: '2px 4px', borderRadius: 5, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} defaultValue={settings.reassessment_days}
                  onBlur={e => { const v = Number(e.target.value); if (v > 0 && v !== settings.reassessment_days) saveClinicalSettings({ reassessment_days: v }).then(setSettings); }} /> d
              </span>
            )}
          </div>
        </div>
        <DataTable
          tableId="nurse-assessments"
          columns={columns}
          data={filtered}
          getRowId={c => c.id}
          onRowClick={c => navigate(`/clients/${c.id}`)}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          manualSorting
          loading={loading}
          emptyMessage="No clients match this filter."
          maxHeight="calc(100vh - 320px)"
        />
      </div>

      {/* ── Revoke Toast ── */}
      <Toast {...(toast || {})} onClose={hideToast} />

      {/* ── Revoke Confirm Modal ── */}
      {revokeModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setRevokeModal(null)}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 400, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700, color: 'var(--danger)', marginBottom: 8 }}>
              Revoke Assessment Link
            </div>
            <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 20 }}>
              Revoke the active assessment link for <strong>{revokeModal.first_name} {revokeModal.last_name}</strong>? The nurse will no longer be able to access it.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setRevokeModal(null)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={handleRevoke} disabled={revoking} style={{ flex: 2, padding: '10px 0', background: revoking ? 'var(--border2)' : 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: revoking ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {revoking ? 'Revoking…' : 'Confirm Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Send Link Modal ── */}
      {linkModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={closeLinkModal}>
          <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}
            onClick={e => e.stopPropagation()}>

            <div style={{ fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 700, color: 'var(--accent)', marginBottom: 4 }}>
              Send Assessment Link
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
              Patient: <strong>{linkModal.last_name}, {linkModal.first_name}</strong> ({linkModal.client_id})
            </div>

            {linkResult === 'sent' ? (
              <div>
                <div style={{ background: 'var(--success-bg)', border: '1px solid var(--success)', borderRadius: 8, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: 'var(--success)', lineHeight: 1.6 }}>
                  ✅ <strong>Link sent successfully</strong> to <strong>{nurseEmail}</strong>.<br />
                  The link expires in 7 days. Once the nurse submits the assessment, it will appear in CAERA and the link will be invalidated.
                </div>
                <button onClick={closeLinkModal} style={{ width: '100%', padding: '10px 0', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                  Done
                </button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 5 }}>Nurse Email Address *</label>
                  <input
                    type="email"
                    value={nurseEmail}
                    onChange={e => setNurseEmail(e.target.value)}
                    placeholder="nurse@example.com"
                    style={{ width: '100%', border: '1.5px solid var(--accent-border)', borderRadius: 6, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                    autoComplete="new-password"
                    name="nurse_email_field"
                    autoFocus
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: 'var(--accent)', marginBottom: 5 }}>Nurse Name (optional)</label>
                  <input
                    type="text"
                    value={nurseName}
                    onChange={e => setNurseName(e.target.value)}
                    placeholder="e.g. Jane Smith, RN"
                    style={{ width: '100%', border: '1.5px solid var(--accent-border)', borderRadius: 6, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
                  />
                </div>

                <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn)', borderRadius: 6, padding: '8px 12px', marginBottom: 18, fontSize: 11, color: 'var(--warn)' }}>
                  The nurse will receive a secure, one-time link that expires in <strong>7 days</strong>. Once they submit, the link is invalidated.
                </div>

                {linkResult === 'error' && (
                  <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger)', borderRadius: 6, padding: '8px 12px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>
                    ⚠ {linkError}
                  </div>
                )}

                {nurseEmail.trim() && (
                  <div style={{ fontSize: 11, color: 'var(--accent)', marginBottom: 12, background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 6, padding: '6px 10px' }}>
                    📧 Will send to: <strong>{nurseEmail.trim().toLowerCase()}</strong>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={closeLinkModal} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border2)', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleSendLink}
                    disabled={linkSending || !nurseEmail.trim()}
                    style={{ flex: 2, padding: '10px 0', background: linkSending || !nurseEmail.trim() ? 'var(--border2)' : 'var(--success)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: linkSending || !nurseEmail.trim() ? 'default' : 'pointer', fontFamily: 'inherit' }}
                  >
                    {linkSending ? 'Sending…' : '✉ Send Assessment Link'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
